import {type Repository, useCurrentRepository} from '@github-ui/current-repository'
import {tagPath} from '@github-ui/paths'
import {KebabHorizontalIcon, TagIcon} from '@primer/octicons-react'
import {ActionList, Box, IconButton, Link, Octicon} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {useCallback, useRef, useState} from 'react'

import type {BranchCommitState} from '../../hooks/use-load-branch-commits'

export interface CommitTagInfoProps {
  data: BranchCommitState
}

export function CommitTagInfo({data}: CommitTagInfoProps) {
  const repo = useCurrentRepository()

  if (data.tags.length === 0) {
    return null
  }

  const renderTagsInLine = data.tags.length > 2 && data.tags.length < 25
  const renderTagsInDialog = data.tags.length >= 25

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        borderBottomStyle: 'solid',
        borderColor: 'border.default',
        borderWidth: '1px',
        flexWrap: 'wrap',
        p: 3,
        gap: 2,
      }}
    >
      <Octicon icon={TagIcon} sx={{mr: 2}} />

      <Link href={tagPath({owner: repo.ownerLogin, repo: repo.name, tag: data.tags[0]!})} sx={{color: 'fg.default'}}>
        {data.tags[0]}
      </Link>

      {renderTagsInLine ? <ExpandedTags tags={data.tags} repo={repo} /> : null}
      {renderTagsInDialog ? <CommitTagsDialog tags={data.tags} repo={repo} /> : null}

      {data.tags.length > 1 ? (
        <Link
          href={tagPath({owner: repo.ownerLogin, repo: repo.name, tag: data.tags[data.tags.length - 1]!})}
          sx={{color: 'fg.subtle'}}
        >
          {data.tags[data.tags.length - 1]}
        </Link>
      ) : null}
    </Box>
  )
}

function ExpandedTags({tags, repo}: {tags: string[]; repo: Repository}) {
  const [tagsExpanded, setTagsExpanded] = useState(false)
  const firstExpandedTagRef = useRef<HTMLAnchorElement>(null)

  const onExpand = useCallback(() => {
    setTagsExpanded(true)
    requestAnimationFrame(() => firstExpandedTagRef.current?.focus())
  }, [])

  return tagsExpanded ? (
    <>
      {tags.slice(1, tags.length - 1).map((tag, index) => {
        return (
          <Link
            ref={index === 0 ? firstExpandedTagRef : null}
            href={tagPath({owner: repo.ownerLogin, repo: repo.name, tag})}
            sx={{color: 'fg.subtle'}}
            key={tag}
          >
            {tag}
          </Link>
        )
      })}
    </>
  ) : (
    <IconButton
      aria-label="Show more tags"
      icon={KebabHorizontalIcon}
      size="small"
      variant="invisible"
      onClick={onExpand}
    />
  )
}

function CommitTagsDialog({tags, repo}: {tags: string[]; repo: Repository}) {
  const [dialogOpen, setDialogOpen] = useState(false)
  const returnFocusRef = useRef(null)

  return (
    <>
      <IconButton
        aria-label="Show all tags"
        icon={KebabHorizontalIcon}
        size="small"
        variant="invisible"
        onClick={() => setDialogOpen(true)}
        ref={returnFocusRef}
      />
      {dialogOpen && (
        <Dialog
          title={`${tags.length} tags`}
          onClose={() => setDialogOpen(false)}
          returnFocusRef={returnFocusRef}
          width="medium"
          height={tags.length >= 12 ? 'small' : 'auto'}
          renderBody={() => {
            return (
              <ActionList sx={{overflowY: 'auto', py: 2}} data-testid="tag-dialog-list">
                {tags.map(tag => (
                  <ActionList.LinkItem key={tag} href={tagPath({owner: repo.ownerLogin, repo: repo.name, tag})}>
                    {tag}
                  </ActionList.LinkItem>
                ))}
              </ActionList>
            )
          }}
        />
      )}
    </>
  )
}

try{ CommitTagInfo.displayName ||= 'CommitTagInfo' } catch {}
try{ ExpandedTags.displayName ||= 'ExpandedTags' } catch {}
try{ CommitTagsDialog.displayName ||= 'CommitTagsDialog' } catch {}